import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    invalidateCloseHealthQueries,
    invalidateUpdatedTaskQueries,
    isTaskBulkUpdateApplied,
    requestTaskUpdate,
    restoreTaskFromUpdateSnapshot,
    useBulkUpdateTasks,
} from "@api/task.api";
import type { TaskBulkUpdate } from "@common/contracts/task.contract";
import { pluralizeWithCount } from "@common/helpers/string";
import type { Task } from "@common/types/task";
import type {
    BoardColumnDef,
    BoardMoveBatch,
} from "@design-system/Board/Board.types";
import { describePartialDrop } from "@design-system/Board/boardGroupAnnouncements";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    getTaskBoardMoveUpdate,
    type TaskBoardAxes,
    type TaskBoardMoveBody,
} from "./taskBoardMoves";
import type { TaskBoardSort } from "./taskBoardSort";
import type { RestoreDroppedColumns } from "./useTaskBoardCollapse";
import {
    type TaskBoardSaveRequest,
    useTaskBoardSave,
} from "./useTaskBoardSave";
import { useTaskCompleter } from "./useTaskCompleter";

/**
 * The bulk update a drop's fields stand for: one field the bulk endpoint
 * takes. Null for the rest (due date, stage, an entity swap, a column and a
 * lane at once), which go out per task.
 */
const getTaskBulkAction = (
    body: TaskBoardMoveBody,
): TaskBulkUpdate["action"] | null => {
    if (Object.keys(body).length !== 1) {
        return null;
    }
    if (body.status !== undefined) {
        return { type: "status", status: body.status };
    }
    if (body.priority !== undefined) {
        return { type: "priority", priority: body.priority };
    }
    if (body.assigneeId !== undefined) {
        return { type: "assignee", assigneeId: body.assigneeId };
    }
    if (body.groupId !== undefined) {
        return { type: "taskGroup", groupId: body.groupId };
    }
    return null;
};

/** What a group save leaves to put back, and whether to refetch its tasks yet. */
interface TaskGroupSaveResult {
    failedRequests: readonly TaskBoardSaveRequest[];
    /** False while a bulk job runs: its progress events patch the cache. */
    shouldRefetch: boolean;
}

/** "Moved 3 tasks, 2 failed", or "Failed to move 5 tasks" when none saved. */
const describeFailedMoves = ({
    count,
    failedCount,
}: {
    count: number;
    failedCount: number;
}) =>
    failedCount === count
        ? `Failed to move ${pluralizeWithCount("task", count)}`
        : `Moved ${pluralizeWithCount("task", count - failedCount)}, ${failedCount} failed`;

interface UseTaskBoardGroupMoveArgs {
    tasks: readonly Task[];
    axes: TaskBoardAxes;
    /** The columns and lanes, to name a new assignee. */
    axisValues: readonly BoardColumnDef[];
    sort: TaskBoardSort;
    /** Cards of the drop landed in the cache in these columns; undone where none saved. */
    onMoveApplied: (columnIds: readonly string[]) => RestoreDroppedColumns;
}

/**
 * Group drag on the workspace board (`onMoveMany`). Each card moves by the
 * single-drop rules, all of them land in the cache at once, and they save
 * in one bulk update when they share one field, else per task. A failed
 * bulk update puts every card back with "Failed to move {n} tasks"; per
 * task, only the failed cards go back, with "Moved {m} tasks, {k} failed".
 * `onMoveApplied` is undone for columns none of whose cards saved. A bulk
 * update the server runs as a job isn't refetched: its progress
 * events patch the cache. Cards the target refused stay, and a toast says
 * why. Like List's bulk Set status, a move into Done or Canceled doesn't
 * ask about blocked tasks.
 */
export const useTaskBoardGroupMove = (args: UseTaskBoardGroupMoveArgs) => {
    const queryClient = useQueryClient();
    const toaster = useToaster();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { applyUpdate, revert } = useTaskBoardSave("Failed to move task");
    const { mutateAsync: bulkUpdateTasks } = useBulkUpdateTasks();
    const completer = useTaskCompleter();
    const latestRef = useLatestRef({
        ...args,
        completer,
        toaster,
        queryClient,
    });

    const saveMoves = useCallback(
        async (
            requests: readonly TaskBoardSaveRequest[],
        ): Promise<TaskGroupSaveResult> => {
            const bodyKeys = new Set(
                requests.map(({ body }) => JSON.stringify(body)),
            );
            const action =
                bodyKeys.size === 1
                    ? getTaskBulkAction(requests[0].body)
                    : null;
            if (action !== null) {
                try {
                    const response = await bulkUpdateTasks({
                        taskIds: requests.map(({ task }) => task.id),
                        action,
                    });
                    return {
                        failedRequests: [],
                        shouldRefetch: isTaskBulkUpdateApplied(response),
                    };
                } catch {
                    return { failedRequests: requests, shouldRefetch: true };
                }
            }
            const results = await Promise.allSettled(
                requests.map(({ task, body }) =>
                    requestTaskUpdate({ task, body }),
                ),
            );
            return {
                failedRequests: requests.filter(
                    (_request, index) => results[index].status === "rejected",
                ),
                shouldRefetch: true,
            };
        },
        [bulkUpdateTasks],
    );

    /** Saves the drop's requests and resolves with those that failed, already put back. */
    const persist = useCallback(
        async (
            requests: readonly TaskBoardSaveRequest[],
        ): Promise<readonly TaskBoardSaveRequest[]> => {
            const latest = latestRef.current;
            const { failedRequests, shouldRefetch } = await saveMoves(requests);
            if (failedRequests.length === requests.length) {
                for (const request of requests.toReversed()) {
                    revert(request);
                }
            } else {
                for (const { task, snapshot } of failedRequests) {
                    restoreTaskFromUpdateSnapshot({
                        queryClient: latest.queryClient,
                        snapshot,
                        taskId: task.id,
                    });
                }
            }
            if (failedRequests.length > 0) {
                latest.toaster.error(
                    describeFailedMoves({
                        count: requests.length,
                        failedCount: failedRequests.length,
                    }),
                );
            }
            if (!shouldRefetch) {
                return failedRequests;
            }
            for (const { task, body } of requests) {
                invalidateUpdatedTaskQueries({
                    queryClient: latest.queryClient,
                    workspaceKey: task.workspaceId,
                    taskId: task.id,
                });
                invalidateCloseHealthQueries({
                    queryClient: latest.queryClient,
                    task,
                    body,
                });
            }
            return failedRequests;
        },
        [latestRef, saveMoves, revert],
    );

    return useCallback(
        (batch: BoardMoveBatch<Task>) => {
            if (refuseIfViewOnly()) {
                return;
            }
            const latest = latestRef.current;
            if (batch.refusals.length > 0) {
                latest.toaster.toast(
                    describePartialDrop({
                        noun: "task",
                        movedCount: batch.moves.length,
                        refusals: batch.refusals,
                    }),
                );
            }
            const drops = batch.moves.flatMap((move) => {
                const update = getTaskBoardMoveUpdate({ ...latest, move });
                return update === null
                    ? []
                    : [
                          {
                              request: applyUpdate({
                                  task: move.item,
                                  ...update,
                              }),
                              columnId: move.to.columnId,
                          },
                      ];
            });
            if (drops.length === 0) {
                return;
            }
            const droppedColumnIds = drops.map(({ columnId }) => columnId);
            const restoreCollapse = latest.onMoveApplied(droppedColumnIds);
            void persist(drops.map(({ request }) => request)).then(
                (failedRequests) => {
                    const savedColumnIds = new Set(
                        drops
                            .filter(
                                ({ request }) =>
                                    !failedRequests.includes(request),
                            )
                            .map(({ columnId }) => columnId),
                    );
                    restoreCollapse(
                        droppedColumnIds.filter(
                            (columnId) => !savedColumnIds.has(columnId),
                        ),
                    );
                },
            );
        },
        [refuseIfViewOnly, latestRef, applyUpdate, persist],
    );
};
